import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function WebhooksManager() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    webhook_url: '',
    message_format: 'text',
    send_images: true,
    send_videos: true,
    send_content: true,
    filter_duplicates: false,
    enabled: true,
    api_secret_key: ''
  });

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      const response = await api.get('/webhooks');
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      toast.error('שגיאה בטעינת webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWebhook = async () => {
    if (!newWebhook.name || !newWebhook.webhook_url) {
      toast.error('יש למלא שם וכתובת webhook');
      return;
    }

    if (webhooks.length >= 5) {
      toast.error('ניתן ליצור עד 5 webhooks בלבד');
      return;
    }

    try {
      await api.post('/webhooks', newWebhook);
      toast.success('Webhook נוסף בהצלחה');
      setNewWebhook({
        name: '',
        webhook_url: '',
        send_content: true,
        send_images: true,
        send_videos: true,
        filter_duplicates: false,
        message_format: 'text',
        api_secret_key: ''
      });
      loadWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בהוספת webhook');
    }
  };

  const handleUpdateWebhook = async (id, updates) => {
    try {
      await api.put(`/webhooks/${id}`, updates);
      toast.success('Webhook עודכן');
      loadWebhooks();
      setEditingId(null);
    } catch (error) {
      toast.error('שגיאה בעדכון webhook');
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm('האם למחוק webhook זה?')) {
      return;
    }

    try {
      await api.delete(`/webhooks/${id}`);
      toast.success('Webhook נמחק');
      loadWebhooks();
    } catch (error) {
      toast.error('שגיאה במחיקת webhook');
    }
  };

  const handleToggleEnabled = async (id, currentState) => {
    await handleUpdateWebhook(id, { enabled: !currentState });
  };

  if (loading) {
    return <div className="text-center py-4">טוען...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          <Globe className="inline-block w-6 h-6 ml-2" />
          Webhooks מותאמים אישית
        </h2>
        <span className="text-sm text-gray-500">
          {webhooks.length} / 5 webhooks
        </span>
      </div>

      {/* טופס הוספת webhook חדש */}
      {webhooks.length < 5 && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold mb-4 text-blue-900">הוסף Webhook חדש</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                שם Webhook
              </label>
              <input
                type="text"
                value={newWebhook.name}
                onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                placeholder="לדוגמה: Google Chat"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                כתובת Webhook URL
              </label>
              <input
                type="url"
                value={newWebhook.webhook_url}
                onChange={(e) => setNewWebhook({ ...newWebhook, webhook_url: e.target.value })}
                placeholder="https://chat.googleapis.com/v1/spaces/..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                dir="ltr"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-4 flex-wrap">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newWebhook.send_content}
                onChange={(e) => setNewWebhook({ ...newWebhook, send_content: e.target.checked })}
                className="ml-2"
              />
              <span className="text-sm">שלח תוכן</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newWebhook.send_images}
                onChange={(e) => setNewWebhook({ ...newWebhook, send_images: e.target.checked })}
                className="ml-2"
              />
              <span className="text-sm">שלח תמונות</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newWebhook.send_videos}
                onChange={(e) => setNewWebhook({ ...newWebhook, send_videos: e.target.checked })}
                className="ml-2"
              />
              <span className="text-sm">שלח סרטונים</span>
            </label>

            <label className="flex items-center" title="סינון כפילויות - לא ישלח הודעות שכבר נשלחו (כמו בטלפון)">
              <input
                type="checkbox"
                checked={newWebhook.filter_duplicates}
                onChange={(e) => setNewWebhook({ ...newWebhook, filter_duplicates: e.target.checked })}
                className="ml-2"
              />
              <span className="text-sm font-medium text-orange-700">סינון כפילויות</span>
            </label>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                פורמט הודעה
              </label>
              <select
                value={newWebhook.message_format}
                onChange={(e) => setNewWebhook({ ...newWebhook, message_format: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="text">טקסט רגיל</option>
                <option value="google_cards">כרטיסיות Google Chat</option>
                <option value="netlify">Netlify API</option>
              </select>
            </div>

            {newWebhook.message_format === 'netlify' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Secret Key (אופציונלי)
                </label>
                <input
                  type="text"
                  value={newWebhook.api_secret_key}
                  onChange={(e) => setNewWebhook({ ...newWebhook, api_secret_key: e.target.value })}
                  placeholder="הכנס מפתח API או השאר ריק"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  dir="ltr"
                />
                <p className="text-xs text-gray-500 mt-1">
                  אם יש צורך במפתח API לאימות, הכנס כאן
                </p>
              </div>
            )}
          </div>

          <button
            onClick={handleAddWebhook}
            className="mt-4 flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 ml-2" />
            הוסף Webhook
          </button>
        </div>
      )}

      {/* רשימת webhooks קיימים */}
      <div className="space-y-4">
        {webhooks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            אין webhooks. לחץ על "הוסף Webhook" כדי להתחיל.
          </div>
        ) : (
          webhooks.map((webhook) => (
            <div
              key={webhook.id}
              className={`p-4 border rounded-lg ${
                webhook.enabled ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {editingId === webhook.id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        defaultValue={webhook.name}
                        onBlur={(e) => handleUpdateWebhook(webhook.id, { name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="url"
                        defaultValue={webhook.webhook_url}
                        onBlur={(e) => handleUpdateWebhook(webhook.id, { webhook_url: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        dir="ltr"
                      />
                    </div>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">{webhook.name}</h3>
                      <p className="text-sm text-gray-600 font-mono mt-1" dir="ltr">
                        {webhook.webhook_url}
                      </p>
                    </>
                  )}

                  <div className="mt-3 flex gap-4 text-sm flex-wrap">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={webhook.send_content}
                        onChange={(e) => handleUpdateWebhook(webhook.id, { send_content: e.target.checked })}
                        className="ml-2"
                      />
                      <span>תוכן</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={webhook.send_images}
                        onChange={(e) => handleUpdateWebhook(webhook.id, { send_images: e.target.checked })}
                        className="ml-2"
                      />
                      <span>תמונות</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={webhook.send_videos}
                        onChange={(e) => handleUpdateWebhook(webhook.id, { send_videos: e.target.checked })}
                        className="ml-2"
                      />
                      <span>סרטונים</span>
                    </label>

                    <label className="flex items-center" title="סינון כפילויות - לא ישלח הודעות שכבר נשלחו">
                      <input
                        type="checkbox"
                        checked={!!webhook.filter_duplicates}
                        onChange={(e) => handleUpdateWebhook(webhook.id, { filter_duplicates: e.target.checked })}
                        className="ml-2"
                      />
                      <span className="font-medium text-orange-700">סינון</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleEnabled(webhook.id, webhook.enabled)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      webhook.enabled
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-400 text-white hover:bg-gray-500'
                    }`}
                  >
                    {webhook.enabled ? 'פעיל' : 'כבוי'}
                  </button>

                  <button
                    onClick={() => setEditingId(editingId === webhook.id ? null : webhook.id)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                  >
                    <Save className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => handleDeleteWebhook(webhook.id)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="mt-2 text-xs text-gray-500">
                נוצר: {new Date(webhook.created_at).toLocaleString('he-IL')}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
